import http, { flushClientCaches } from "@/helper/http";
import api from "@/helper/api";

const commitProviderResponse = (commit, response) => {
  const data = response?.data;
  if (
    (data?.status === "authenticated" || data?.status === "created") &&
    data.token &&
    data.user
  ) {
    commit("SET_RESPONSE", data);
    commit("SET_AUTH_USER", data.user);
  }
  return data;
};

export default {
  state: {
    loading: false,
    error: [],
    response: [],
    authenticatedUser: null,
    loadingUser: false,
  },
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_LOADING_USER(state, loadingUser) {
      state.loadingUser = loadingUser;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_RESPONSE(state, response) {
      state.response = response;
    },
    SET_AUTH_USER(state, authenticatedUser) {
      state.authenticatedUser = authenticatedUser;
    },
  },
  actions: {
    requestSMS({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.request_sms, payload)
          .then((response) => {
            commit("SET_RESPONSE", response?.data);
            commit("SET_LOADING", false);
            resolve(response?.data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "An error occurred while sending an SMS, Please try again",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    verifyOTP({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.verify_code, payload)
          .then((response) => {
            commit("SET_RESPONSE", response?.data);
            commit("SET_LOADING", true);

            resolve(response?.data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "An error occurred while verifying the OTP, Please try again",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    googleLogin({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.google_login, payload)
          .then((response) => {
            const data = commitProviderResponse(commit, response);
            commit("SET_LOADING", false);
            resolve(data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "Google sign-in failed. Please try again.",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    appleLogin({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.apple_login, payload)
          .then((response) => {
            const data = commitProviderResponse(commit, response);
            commit("SET_LOADING", false);
            resolve(data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "Apple sign-in failed. Please try again.",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    googleLinkAccount({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.google_link, payload)
          .then((response) => {
            if (response?.data?.user) {
              commit("SET_AUTH_USER", response.data.user);
            }
            commit("SET_LOADING", false);
            resolve(response?.data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "Failed to link Google account. Please try again.",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    appleLinkAccount({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.apple_link, payload)
          .then((response) => {
            if (response?.data?.user) {
              commit("SET_AUTH_USER", response.data.user);
            }
            commit("SET_LOADING", false);
            resolve(response?.data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "Failed to link Apple account. Please try again.",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    verifyProvider2FA({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .post(api.provider_2fa_verify, payload)
          .then((response) => {
            const data = commitProviderResponse(commit, response);
            commit("SET_LOADING", false);
            resolve(data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "Two-factor verification failed. Please try again.",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    update2FASettings({ commit }, payload) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .put(api.two_factor_settings, payload)
          .then((response) => {
            commit("SET_LOADING", false);
            resolve(response?.data);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.response?.data?.message ?? "Failed to update 2FA settings.",
            );
            commit("SET_LOADING", false);
            reject(error?.response);
          });
      });
    },
    get2FASettings() {
      return new Promise((resolve, reject) => {
        http
          .get(api.two_factor_settings)
          .then((response) => {
            resolve(response?.data);
          })
          .catch((error) => {
            reject(error?.response);
          });
      });
    },
    checkUserPermissions({ commit }) {
      commit("SET_LOADING", true);
      return new Promise((resolve, reject) => {
        http
          .get(api.verify_admin, {
            headers: {},
          })
          .then((response) => {
            const permissions = response.data.permissions;
            commit("SET_LOADING", false);
            resolve(permissions);
          })
          .catch((error) => {
            commit("SET_LOADING", false);
            reject(error);
          });
      });
    },
    getUserInfo({ state, commit }) {
      const existingUser = state.authenticatedUser;
      const hasExistingUser =
        !!existingUser &&
        ((Array.isArray(existingUser) && existingUser.length > 0) ||
          (typeof existingUser === "object" &&
            Object.keys(existingUser).length > 0));

      if (hasExistingUser) {
        return Promise.resolve(existingUser);
      }

      commit("SET_LOADING_USER", true);
      return new Promise((resolve, reject) => {
        http
          .get(api.authenticated_user)
          .then((response) => {
            commit("SET_AUTH_USER", response?.data ?? null);
            commit("SET_LOADING_USER", false);
            resolve(response);
          })
          .catch((error) => {
            commit("SET_LOADING_USER", false);
            reject(error);
          });
      });
    },
    logoutUser({ commit }) {
      commit("SET_LOADING", true);

      const clearClientState = () => {
        commit("SET_AUTH_USER", null);
        commit("SET_RESPONSE", []);
        commit("SET_ERROR", []);
        try {
          flushClientCaches();
        } catch (e) {
          void e;
        }
      };

      const redirectToLogin = () => {
        if (typeof window !== "undefined") {
          window.location.replace("/");
        }
      };

      // Clear local storage FIRST before making API call
      // This ensures user is logged out locally even if API fails
      clearClientState();

      return new Promise((resolve, reject) => {
        http
          .post(api.logout)
          .then((response) => {
            commit("SET_LOADING", false);
            redirectToLogin();
            resolve(response?.data);
          })
          .catch((error) => {
            const status = error?.response?.status;
            commit("SET_LOADING", false);

            if (status === 401) {
              redirectToLogin();
              resolve(null);
              return;
            }

            commit(
              "SET_ERROR",
              error.response?.data?.message ??
                "Unexpected error occurred, Please try again",
            );
            redirectToLogin();
            reject(error?.response);
          });
      });
    },
  },
  getters: {
    isLoading: (state) => state.loading,
    isLoadingUser: (state) => state.loadingUser,
    getError: (state) => state.error,
    getResponse: (state) => state.response,
    getAuthenticatedUser: (state) => state.authenticatedUser,
    hasAdminPermissions: (state) => {
      const permissions = state.authenticatedUser?.admin_permissions;
      return Array.isArray(permissions) && permissions.length > 0;
    },
  },
};
